import moment from "moment";

// Intl gives French day/month names without changing moment's global locale for the rest of the app
const dayFormatter = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
const dayWithYearFormatter = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

export const formatTime = (value) => moment(value).format('HH:mm');

export function formatRelative(value, now = moment()) {
  const minutes = moment(now).diff(moment(value), 'minutes');
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  return `il y a ${Math.floor(hours / 24)} j`;
}

export function formatDayLabel(value, now = moment()) {
  const date = moment(value);
  if (date.isSame(now, 'day')) return "Aujourd'hui";
  if (date.isSame(moment(now).subtract(1, 'day'), 'day')) return 'Hier';
  const formatter = date.isSame(now, 'year') ? dayFormatter : dayWithYearFormatter;
  return capitalize(formatter.format(date.toDate()));
}

export const formatFullDate = (value) =>
  `${capitalize(dayWithYearFormatter.format(moment(value).toDate()))} à ${formatTime(value)}`;

export const isStatusMessage = (message) => Boolean(message?.contenu?.includes('Message_Update'));

export const isSameSender = (a, b) => String(a?.sender?.id) === String(b?.sender?.id);

/**
 * Turns the raw message list into what the chat displays:
 * day separators, status events and messages. Consecutive messages from the same sender
 * within the same minute share one header (name + time).
 */
export function buildTimeline(messages, userId) {
  const sorted = [...(messages || [])].sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
  const items = [];
  let lastDay = null;
  let previous = null;

  sorted.forEach((message, index) => {
    if (!message.attachement?.id && !String(message.contenu ?? '').trim()) return;
    const date = moment(message.date);
    const day = date.format('YYYY-MM-DD');
    const key = message.id ?? message.localId ?? `message-${index}`;

    if (day !== lastDay) {
      items.push({ type: 'day', key: `day-${day}`, date: message.date });
      lastDay = day;
      previous = null;
    }

    if (isStatusMessage(message)) {
      items.push({ type: 'status', key, message });
      previous = null;
      return;
    }

    const startsGroup = !previous
      || !isSameSender(previous, message)
      || moment(previous.date).format('HH:mm') !== date.format('HH:mm');

    items.push({
      type: 'message',
      key,
      message,
      isOwn: String(message.sender?.id) === String(userId),
      startsGroup,
    });
    previous = message;
  });

  return items;
}

export function displayName(name) {
  const clean = (name || '').trim();
  if (!clean) return 'Utilisateur';
  return clean.split(/([\s-]+)/).map(capitalize).join('');
}

export function initials(name) {
  return displayName(name)
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase();
}

const AVATAR_COLORS = [
  { bg: '#E8F1FC', fg: '#1671D9' },
  { bg: '#EFEBFE', fg: '#6941C6' },
  { bg: '#E6F6F4', fg: '#0E7C73' },
  { bg: '#FDEBF4', fg: '#C11574' },
  { bg: '#FEF0E6', fg: '#C4320A' },
  { bg: '#EEF0F3', fg: '#475467' },
];

export function avatarColor(seed) {
  const text = String(seed ?? '');
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

export function attachmentKind(attachment) {
  const type = (attachment?.type || '').toLowerCase();
  const extension = (attachment?.nom || '').toLowerCase().split('.').pop();
  if (type.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension)) return 'image';
  if (type.startsWith('audio/') || ['mp3', 'ogg', 'oga', 'wav', 'm4a', 'aac', 'opus'].includes(extension)) return 'audio';
  if (type === 'application/pdf' || extension === 'pdf') return 'pdf';
  if (['xls', 'xlsx', 'csv'].includes(extension)) return 'sheet';
  if (['doc', 'docx'].includes(extension)) return 'document';
  return 'file';
}
